/**
 * M4 社区后台治理 API 封装（03 号文档第 7 节、07 号文档 2.4/第 5 节）。
 *
 * 对应后端控制器：
 * - admin-community.controller.ts（审核队列/详情/动作/批量通过/处置记录/统计）
 * - admin-report.controller.ts（举报队列/详情/处理）
 * - admin-sensitive-word.controller.ts（敏感词 CRUD）
 *
 * 禁止 Mock：所有函数均直接调用 apiRequest，返回结构严格对应服务端真实字段。
 */
import type { ApiMeta } from '@liyu/shared-types';
import { apiRequest } from './client';
import type {
  AdminCommunityTopicListQuery,
  AdminCommunityTopicRow,
  AdminCommunityTopicUpsertPayload,
  Paginated,
} from './types';

export type ModerationStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REMOVED';
export type RiskLevel = 'LOW' | 'HIGH';
export type ModerationTargetType = 'POST' | 'COMMENT';
export type PostType = 'OPINION' | 'RESEARCH' | 'ACTIVITY_REVIEW' | 'QA' | 'NEWS';
export type ReportStatus = 'PENDING' | 'PROCESSING' | 'RESOLVED' | 'REJECTED';
export type ReportAction =
  'NO_VIOLATION' | 'CONTENT_REMOVED' | 'CONTENT_DELETED' | 'USER_MUTED' | 'USER_BANNED';
export type ReportReason =
  'SPAM' | 'MISLEADING' | 'HARASSMENT' | 'ILLEGAL' | 'INFRINGEMENT' | 'OTHER';
export type AccountStatus = 'ACTIVE' | 'MUTED' | 'BANNED' | 'DELETED';

export interface CommunityAuthor {
  id: string;
  displayName: string;
  liyuNo: string;
  userType: 'USER' | 'KOL';
  accountStatus: AccountStatus;
}

export interface AdminModerationPost {
  id: string;
  type: PostType;
  typeLabel: string;
  body: string;
  status: ModerationStatus;
  statusLabel: string;
  riskLevel: RiskLevel;
  riskNote: string | null;
  isOfficial: boolean;
  author: CommunityAuthor;
  imageCount: number;
  tagNames: string[];
  likeCount: number;
  commentCount: number;
  reportCount: number;
  createdAt: string;
  publishedAt: string | null;
}

export interface AdminModerationPostDetail extends Omit<
  AdminModerationPost,
  'imageCount' | 'author'
> {
  author: CommunityAuthor & { violationCount: number };
  images: Array<{ id: string; fileId: string; url: string; isPublic: boolean }>;
  tags: Array<{ id: string; name: string }>;
  reviewedAt: string | null;
  reviewedByAdminId: string | null;
  reviewNote: string | null;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AdminModerationComment {
  id: string;
  postId: string;
  postSummary: string;
  parentCommentId: string | null;
  body: string;
  status: ModerationStatus;
  statusLabel: string;
  riskLevel: RiskLevel;
  riskNote: string | null;
  author: CommunityAuthor;
  likeCount: number;
  replyCount: number;
  reportCount: number;
  createdAt: string;
  publishedAt: string | null;
}

export interface AdminModerationCommentDetail extends Omit<
  AdminModerationComment,
  'postSummary' | 'author'
> {
  post: { id: string; summary: string; status: ModerationStatus; deletedAt: string | null };
  author: CommunityAuthor & { violationCount: number };
  reviewedAt: string | null;
  reviewedByAdminId: string | null;
  reviewNote: string | null;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ModerationActionView {
  id: string;
  status: ModerationStatus;
  statusLabel: string;
  changed: boolean;
}

export interface ModerationDeleteView {
  id: string;
  deleted: boolean;
  changed: boolean;
}

export interface BatchApproveResult {
  approved: number;
  skipped: Array<{ id: string; reason: string }>;
}

export interface ModerationRecordRow {
  id: string;
  targetType: ModerationTargetType;
  targetId: string;
  targetSummary: string | null;
  targetStatus: ModerationStatus | null;
  action: string;
  reason: string | null;
  operatorId: string | null;
  operatorType: string;
  operatorName: string | null;
  beforeStatus: ModerationStatus | null;
  afterStatus: ModerationStatus | null;
  createdAt: string;
}

export interface AdminReportRow {
  id: string;
  targetType: ModerationTargetType;
  targetId: string;
  reason: ReportReason;
  reasonLabel: string;
  detail: string | null;
  status: ReportStatus;
  action: ReportAction | null;
  actionLabel: string | null;
  handleNote: string | null;
  handledByAdminId: string | null;
  handledAt: string | null;
  reporter: CommunityAuthor;
  target: {
    id: string;
    summary: string;
    status: ModerationStatus;
    deleted: boolean;
    author: CommunityAuthor;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface HandleReportPayload {
  action: ReportAction;
  note?: string;
  muteDays?: number;
  banReason?: string;
}

export interface SensitiveWordRow {
  id: string;
  word: string;
  riskLevel: RiskLevel;
  category: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CommunityStats {
  pendingPosts: number;
  highRiskPendingPosts: number;
  pendingComments: number;
  pendingReports: number;
  oldestPendingAt: string | null;
  avgHandleMinutes: number;
}

export interface PageQuery {
  page?: number;
  pageSize?: number;
}

export interface ModerationPostQuery extends PageQuery {
  status?: ModerationStatus;
  riskLevel?: RiskLevel;
  keyword?: string;
}

export interface ModerationCommentQuery extends PageQuery {
  status?: ModerationStatus;
  keyword?: string;
}

export interface ModerationRecordQuery extends PageQuery {
  targetType?: ModerationTargetType;
  targetId?: string;
}

export interface ReportListQuery extends PageQuery {
  status?: ReportStatus;
}

export interface SensitiveWordQuery extends PageQuery {
  keyword?: string;
}

function toPaginated<T>(data: T[], meta?: ApiMeta, fallbackSize = 20): Paginated<T> {
  return {
    items: data,
    total: meta?.total ?? data.length,
    page: meta?.page ?? 1,
    pageSize: meta?.pageSize ?? fallbackSize,
  };
}

// ---------------------------- 审核队列：帖子 ----------------------------

export async function listModerationPosts(
  query: ModerationPostQuery = {},
): Promise<Paginated<AdminModerationPost>> {
  const result = await apiRequest<AdminModerationPost[]>('/community/posts', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      riskLevel: query.riskLevel,
      keyword: query.keyword,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getModerationPost(id: string): Promise<AdminModerationPostDetail> {
  return (await apiRequest<AdminModerationPostDetail>(`/community/posts/${id}`)).data;
}

export async function approvePost(id: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/posts/${id}/approve`, {
      method: 'POST',
      body: {},
    })
  ).data;
}

export async function rejectPost(id: string, reason: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/posts/${id}/reject`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function removePost(id: string, reason: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/posts/${id}/remove`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function deletePost(id: string, reason?: string): Promise<ModerationDeleteView> {
  return (
    await apiRequest<ModerationDeleteView>(`/community/posts/${id}/delete`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  ).data;
}

export async function batchApprovePosts(ids: string[]): Promise<BatchApproveResult> {
  return (
    await apiRequest<BatchApproveResult>('/community/posts/batch-approve', {
      method: 'POST',
      body: { ids },
    })
  ).data;
}

// ---------------------------- 审核队列：评论 ----------------------------

export async function listModerationComments(
  query: ModerationCommentQuery = {},
): Promise<Paginated<AdminModerationComment>> {
  const result = await apiRequest<AdminModerationComment[]>('/community/comments', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      keyword: query.keyword,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getModerationComment(id: string): Promise<AdminModerationCommentDetail> {
  return (await apiRequest<AdminModerationCommentDetail>(`/community/comments/${id}`)).data;
}

export async function approveComment(id: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/comments/${id}/approve`, {
      method: 'POST',
      body: {},
    })
  ).data;
}

export async function rejectComment(id: string, reason: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/comments/${id}/reject`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function removeComment(id: string, reason: string): Promise<ModerationActionView> {
  return (
    await apiRequest<ModerationActionView>(`/community/comments/${id}/remove`, {
      method: 'POST',
      body: { reason },
    })
  ).data;
}

export async function deleteComment(id: string, reason?: string): Promise<ModerationDeleteView> {
  return (
    await apiRequest<ModerationDeleteView>(`/community/comments/${id}/delete`, {
      method: 'POST',
      body: reason ? { reason } : {},
    })
  ).data;
}

// ---------------------------- 处置记录 / 统计 ----------------------------

export async function listModerationRecords(
  query: ModerationRecordQuery = {},
): Promise<Paginated<ModerationRecordRow>> {
  const result = await apiRequest<ModerationRecordRow[]>('/community/moderation-records', {
    query: {
      page: query.page,
      pageSize: query.pageSize,
      targetType: query.targetType,
      targetId: query.targetId,
    },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getCommunityStats(): Promise<CommunityStats> {
  return (await apiRequest<CommunityStats>('/community/stats')).data;
}

// ---------------------------- 举报处理 ----------------------------

export async function listReports(query: ReportListQuery = {}): Promise<Paginated<AdminReportRow>> {
  const result = await apiRequest<AdminReportRow[]>('/community/reports', {
    query: { page: query.page, pageSize: query.pageSize, status: query.status },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function getReport(id: string): Promise<AdminReportRow> {
  return (await apiRequest<AdminReportRow>(`/community/reports/${id}`)).data;
}

export async function handleReport(
  id: string,
  payload: HandleReportPayload,
): Promise<AdminReportRow> {
  return (
    await apiRequest<AdminReportRow>(`/community/reports/${id}/handle`, {
      method: 'POST',
      body: payload,
    })
  ).data;
}

// ---------------------------- 敏感词 ----------------------------

export async function listSensitiveWords(
  query: SensitiveWordQuery = {},
): Promise<Paginated<SensitiveWordRow>> {
  const result = await apiRequest<SensitiveWordRow[]>('/community/sensitive-words', {
    query: { page: query.page, pageSize: query.pageSize, keyword: query.keyword },
  });
  return toPaginated(result.data, result.meta, query.pageSize ?? 20);
}

export async function createSensitiveWord(input: {
  word: string;
  riskLevel?: RiskLevel;
  category?: string;
}): Promise<SensitiveWordRow> {
  return (
    await apiRequest<SensitiveWordRow>('/community/sensitive-words', {
      method: 'POST',
      body: input,
    })
  ).data;
}

export async function updateSensitiveWord(
  id: string,
  input: { riskLevel?: RiskLevel; category?: string; isActive?: boolean },
): Promise<SensitiveWordRow> {
  return (
    await apiRequest<SensitiveWordRow>(`/community/sensitive-words/${id}`, {
      method: 'PATCH',
      body: input,
    })
  ).data;
}

export async function deleteSensitiveWord(id: string): Promise<{ id: string; deleted: boolean }> {
  return (
    await apiRequest<{ id: string; deleted: boolean }>(`/community/sensitive-words/${id}`, {
      method: 'DELETE',
    })
  ).data;
}

// ---------------------------------------------------------------------------
// P1-02 社区身份（官方账号 / KOL 社区直发白名单）
// ---------------------------------------------------------------------------

export interface CommunityIdentityRow {
  id: string;
  liyuNo: string;
  nickname: string | null;
  userType: 'USER' | 'KOL';
  isOfficialAccount: boolean;
  isKol: boolean;
  kolProfileId: string | null;
  kolDisplayName: string | null;
  kolCommunityDirectPost: boolean;
}

export interface CommunityIdentityQuery extends PageQuery {
  keyword?: string;
}

export function fetchCommunityIdentities(
  query: CommunityIdentityQuery = {},
): Promise<Paginated<CommunityIdentityRow>> {
  const params: Record<string, string | number | undefined> = {
    page: query.page,
    pageSize: query.pageSize,
    ...(query.keyword ? { keyword: query.keyword } : {}),
  };
  return apiRequest<CommunityIdentityRow[]>('/community/identity', { query: params }).then(
    (result) => toPaginated(result.data, result.meta),
  );
}

/** 设置/取消官方账号（仅超级管理员，必填原因并写审计） */
export function setOfficialAccount(input: {
  userId: string;
  isOfficialAccount: boolean;
  reason: string;
}): Promise<{ changed: boolean; userId: string; isOfficialAccount: boolean }> {
  return apiRequest<{ changed: boolean; userId: string; isOfficialAccount: boolean }>(
    '/community/identity/official',
    { method: 'POST', body: input },
  ).then((result) => result.data);
}

/** 设置/取消 KOL 社区直发白名单（内容运营/超级管理员，必填原因并写审计） */
export function setKolDirectPost(input: {
  kolId: string;
  enabled: boolean;
  reason: string;
}): Promise<{ changed: boolean; kolId: string; communityDirectPost: boolean }> {
  return apiRequest<{ changed: boolean; kolId: string; communityDirectPost: boolean }>(
    '/community/identity/kol-direct-post',
    { method: 'POST', body: input },
  ).then((result) => result.data);
}

// ---------------------------------------------------------------------------
// 社区话题管理（池塘模块 05 §20–§23）
// 后端：admin-community-topic.controller.ts（MODERATOR / SUPER_ADMIN）
// 只提供「新增 / 修改 / 归档」，不提供物理删除。
// ---------------------------------------------------------------------------

/** 话题列表（含隐藏/归档，可按状态与关键词筛选）；分页字段在 data 内 */
export async function listCommunityAdminTopics(
  query: AdminCommunityTopicListQuery = {},
): Promise<Paginated<AdminCommunityTopicRow>> {
  const result = await apiRequest<Paginated<AdminCommunityTopicRow>>('/community/topics', {
    query: {
      search: query.search,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
  return result.data;
}

/** 新建社区话题（普通用户不能自由创建，仅后台/授权运营） */
export async function createCommunityAdminTopic(
  body: AdminCommunityTopicUpsertPayload,
): Promise<AdminCommunityTopicRow> {
  return (await apiRequest<AdminCommunityTopicRow>('/community/topics', { method: 'POST', body }))
    .data;
}

export async function updateCommunityAdminTopic(
  id: string,
  body: AdminCommunityTopicUpsertPayload,
): Promise<AdminCommunityTopicRow> {
  return (
    await apiRequest<AdminCommunityTopicRow>(`/community/topics/${id}`, { method: 'PATCH', body })
  ).data;
}

/** 归档社区话题（软归档，保留历史讨论与帖子关联；服务端同时清除热门标记） */
export async function archiveCommunityAdminTopic(
  id: string,
): Promise<AdminCommunityTopicRow & { archived: boolean }> {
  return (
    await apiRequest<AdminCommunityTopicRow & { archived: boolean }>(`/community/topics/${id}`, {
      method: 'DELETE',
    })
  ).data;
}
